// ─────────────────────────────────────────────────────────────────────────────
// The translator — key → string. Pure, synchronous, and it never throws.
//
// A key is FULLY QUALIFIED and its first segment is the namespace:
//
//     t('settings.language.title')   → translations/<locale>/settings.json
//                                      → { language: { title: … } }
//
// One rule, no per-call configuration, and a key is enough to find the string
// on disk. That matters more than the keystrokes a scoped `t` would save: the
// question asked in front of a screen is always "where does this text live".
//
// Resolution order for every lookup:
//   1. the active locale — the brand's `strings` overlay, then the catalogue
//   2. FALLBACK_LOCALE (English — the source catalogue, where every key exists),
//      overlay first in the same way
//   3. the key's last segment, humanised
//
// Step 3 exists so a missing key degrades to something a receptionist can read
// rather than to a crash or an empty button. Steps 2 and 3 log once per key in
// development and stay silent in production.
//
// THE BRAND (@parsina/framework/ui/lib/brand). A catalogue value names the
// product as `{{brand}}`, its credit as `{{coin}}` / `{{coinEz}}` and its
// tagline as `{{tagline}}`; those resolve here, in the language of the catalogue
// the text came from, with no value passed by the caller. With no brand
// configured they resolve to the words the catalogue used to spell out, so the
// output is what it always was (src/i18n/brandParity.test.js).
// ─────────────────────────────────────────────────────────────────────────────

import { brandString, brandToken, PLACEHOLDER } from '@parsina/framework/ui/lib/brand';
import { FALLBACK_LOCALE } from './config';

const isDev = process.env.NODE_ENV !== 'production';

// One warning per key per session. A missing key inside a list that renders on
// every keystroke would otherwise bury the console it is trying to inform.
const warned = new Set();

function warnOnce(message) {
  if (!isDev) return;
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(`[i18n] ${message}`);
}

/**
 * `settings.language.title` → `catalog.settings.language.title`, or undefined.
 * Dotted segments only; a value that is not a string or array is treated as a
 * miss, so pointing a key at a whole subtree reports as missing instead of
 * rendering `[object Object]`.
 */
function lookup(catalog, key) {
  if (!catalog || !key) return undefined;
  let node = catalog;
  for (const segment of key.split('.')) {
    if (node == null || typeof node !== 'object') return undefined;
    node = node[segment];
  }
  return typeof node === 'string' || Array.isArray(node) ? node : undefined;
}

/**
 * `lookup`, with the brand's own text for `key` in `locale` asked first — the
 * `strings` overlay a deployment uses for wording a token cannot express.
 */
function find(catalog, locale, key) {
  const own = brandString(locale, key);
  if (typeof own === 'string' || Array.isArray(own)) return own;
  return lookup(catalog, key);
}

/**
 * The catalogue keys holding the IRR unit word — «تومان» / «ت», «Toman» / «T».
 * IRR's words always come from the app's own catalogue, never from the currency
 * table, so the IRR text in every language stays exactly what it was.
 *
 * `inline` is the word as English writes it inside a sentence or a CSV header
 * («12 toman of depreciation», «Price (toman)») where a label says «Toman»; in
 * Persian and Arabic it is the same word as `long`.
 */
export const MONEY_UNIT_KEYS = Object.freeze({
  long: 'common.units.toman',
  inline: 'common.units.tomanInline',
  short: 'common.units.tomanShort',
});

/**
 * `{{name}}` → the matching value. An unknown placeholder is left verbatim so
 * the gap is visible in review rather than silently swallowed.
 *
 * A NUMBER is interpolated through the locale's number formatter, not
 * `String()`. «۳ مورد» is the Persian for "3 items"; «3 مورد» is a half-
 * translated string, and it is the single easiest way for Latin digits to leak
 * into an otherwise Persian screen — every count, total and threshold in the
 * app arrives through this function.
 *
 * Pass a STRING when a number must not be grouped or localised — a year, a
 * version, an ICD code. That is the escape hatch, and it is explicit at the
 * call site rather than inferred from magnitude.
 *
 * Values are inserted as text: this returns a string for React to render as a
 * text node, so there is no HTML parsing and nothing to escape. Interpolating
 * markup is deliberately impossible — a string that needs a bold span is two
 * keys and a component, not a template.
 *
 * `defaults` fill a placeholder the caller did not name (see `defaultValues`).
 *
 * A placeholder the caller did not name may also be a BRAND token (`{{brand}}`,
 * `{{coin}}`, `{{coinEz}}`, `{{tagline}}`, optionally with an inline default:
 * `{{brand|بارسينا}}`) — resolved for `locale`, the language of the template.
 * One pass, so a caller's value that happens to contain `{{brand}}` is inserted
 * as written and never resolved.
 */
function interpolate(template, values, formatNumber, defaults, locale) {
  if (template.indexOf('{{') === -1) return template;
  return template.replace(PLACEHOLDER, (match, name, inline) => {
    // A caller's own value wins whenever the caller NAMED it — even as null, which
    // keeps today's "leave the gap visible" behaviour for a stock `{{unit}}` whose
    // value is missing. Only a name the caller never passed falls to the brand,
    // then to a default.
    const named = values != null && Object.prototype.hasOwnProperty.call(values, name);
    if (!named) {
      const token = brandToken(name, locale, inline);
      if (token !== undefined) return token;
    }
    const value = named ? values[name] : defaults?.[name];
    if (value == null) return match;
    if (typeof value === 'number' && Number.isFinite(value)) return formatNumber(value);
    return String(value);
  });
}

/**
 * Plural suffix for `count`, using the locale's own CLDR rules — Persian has
 * `one`/`other`, English has `one`/`other`, and a language added later with six
 * forms works without touching this file.
 *
 * `key_zero` is honoured ahead of the CLDR category when it exists, because
 * "no appointments" is usually a different sentence rather than a different
 * number, in every language.
 */
function pluralKey(get, key, count, pluralRules) {
  if (count === 0 && get(`${key}_zero`) !== undefined) return `${key}_zero`;
  const category = pluralRules ? pluralRules.select(count) : 'other';
  if (get(`${key}_${category}`) !== undefined) return `${key}_${category}`;
  if (get(`${key}_other`) !== undefined) return `${key}_other`;
  return key;
}

/** `settings.language.title` → `Title`. The last-resort rendering. */
function humanise(key) {
  const last = String(key).split('.').pop() || String(key);
  return last
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Build the `t` for one locale.
 *
 * @param {object} options
 * @param {string} options.locale          active locale id
 * @param {object} options.catalog         { namespace: bundle } for `locale`
 * @param {object} options.fallbackCatalog { namespace: bundle } for FALLBACK_LOCALE
 * @param {Intl.PluralRules} [options.pluralRules]
 * @param {(items: string[]) => string} [options.joinList] locale-correct list
 *        joining (Intl.ListFormat), used when a key's value is an array and it
 *        is read through `t` rather than `tList`.
 * @param {(n: number) => string} [options.formatNumber] locale-correct number
 *        rendering for interpolated numeric values.
 * @param {object|(() => object)} [options.defaultValues] interpolation values a
 *        caller need not pass (a caller's own value always wins). Defaults to the
 *        IRR money unit, `{ unit, unitInline, unitShort }`, read from this catalogue.
 * @param {string} [options.fallbackLocale] the locale `fallbackCatalog` is in —
 *        FALLBACK_LOCALE unless a caller says otherwise. Brand tokens in a string
 *        that fell back resolve in ITS language, not the active one.
 */
export function createTranslator({
  locale, catalog, fallbackCatalog, pluralRules, joinList, formatNumber, defaultValues,
  fallbackLocale = FALLBACK_LOCALE,
}) {
  const join = joinList || ((items) => items.join(', '));
  const num = formatNumber || ((n) => String(n));
  const active = (key) => find(catalog, locale, key);
  const fallback = (key) => find(fallbackCatalog, fallbackLocale, key);

  // Values every string may use without the caller passing them. Today that is
  // the money unit: a catalogue string says «مبلغ ({{unit}})» / «{{amount}}
  // {{unitShort}}» / «{{amount}} {{unitInline}} of …» instead of baking
  // «تومان»/«ت» in, and the unit is the clinic's currency (the clinic's
  // CurrencyProvider re-binds these). Left to itself the translator answers with
  // the catalogue's own IRR words (MONEY_UNIT_KEYS), so an IRR string renders
  // exactly as it did when the word was literal, in every language, with or
  // without a provider.
  // Resolved lazily and once: most strings never ask.
  let defaults = null;
  const defaultsOf = () => {
    if (defaults) return defaults;
    if (defaultValues) {
      defaults = typeof defaultValues === 'function' ? defaultValues() : defaultValues;
    } else {
      const word = (key) => active(key) ?? fallback(key);
      defaults = {
        unit: word(MONEY_UNIT_KEYS.long),
        unitInline: word(MONEY_UNIT_KEYS.inline),
        unitShort: word(MONEY_UNIT_KEYS.short),
      };
    }
    return defaults;
  };

  /**
   * @param {string} key    fully-qualified, e.g. 'common.actions.save'
   * @param {object} [vars] interpolation values; `count` also selects the plural
   * @returns {string}      always a string, even when everything is missing
   */
  function t(key, vars) {
    if (typeof key !== 'string' || !key) {
      warnOnce(`t() called with a non-string key: ${JSON.stringify(key)}`);
      return '';
    }

    const hasCount = vars != null && typeof vars.count === 'number';

    // Active locale.
    let resolved = hasCount ? pluralKey(active, key, vars.count, pluralRules) : key;
    let value = active(resolved);
    let from = locale;

    // Fallback locale.
    if (value === undefined && fallbackCatalog && fallbackCatalog !== catalog) {
      const fallbackResolved = hasCount
        ? pluralKey(fallback, key, vars.count, pluralRules)
        : key;
      value = fallback(fallbackResolved);
      from = fallbackLocale;
      if (value !== undefined) {
        warnOnce(`missing key "${key}" in "${locale}" — fell back to English`);
      }
    }

    // Nothing anywhere.
    if (value === undefined) {
      warnOnce(`missing key "${key}" in every locale — showing the key`);
      return humanise(key);
    }

    // A key whose value is an array joined for `t`; `tList` returns the array.
    if (Array.isArray(value)) {
      return join(value.map((item) => interpolate(String(item), vars, num, defaultsOf(), from)));
    }

    return interpolate(value, vars, num, defaultsOf(), from);
  }

  /**
   * A key whose value is a JSON array, as an array — for bullet lists and
   * option sets, so a translator can add or drop an item without a code change.
   * Always an array, empty when the key is missing.
   */
  function tList(key, vars) {
    let value = active(key);
    let from = locale;
    if (value === undefined) {
      value = fallback(key);
      from = fallbackLocale;
    }
    if (!Array.isArray(value)) {
      if (value === undefined) warnOnce(`missing list key "${key}"`);
      return [];
    }
    return value.map((item) => interpolate(String(item), vars, num, defaultsOf(), from));
  }

  /** Whether a key resolves anywhere — for conditionally-rendered optional copy. */
  function has(key) {
    return active(key) !== undefined || fallback(key) !== undefined;
  }

  return { t, tList, has };
}
